/**
 * Formulario de cadastro do cliente.
 * Reutiliza os campos ja existentes: name, code, cnpj, contact, phone, email, responsibles.
 * Novo cliente: todos os modulos OFF (regra validada).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Save, X } from "lucide-react";
import { useCapabilities } from "@/hooks/useCapabilities";

export default function ClientForm({ client, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    cnpj: "",
    contact: "",
    phone: "",
    email: "",
    responsibles: [],
  });
  const [saving, setSaving] = useState(false);
  const [responsibleInput, setResponsibleInput] = useState("");
  const isNew = !client;
  // Criar/excluir empresa: só a plataforma; editar: clientes.edit (mesma regra de /clients).
  const caps = useCapabilities(isNew ? ["clientes.create"] : ["clientes.edit", "clientes.delete"], {
    clientId: client?.id,
  });
  const canSave = caps.can(isNew ? "clientes.create" : "clientes.edit");
  const canDelete = !isNew && caps.can("clientes.delete");

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        code: client.code || "",
        cnpj: client.cnpj || "",
        contact: client.contact || "",
        phone: client.phone || "",
        email: client.email || "",
        responsibles: Array.isArray(client.responsibles) ? [...client.responsibles] : [],
      });
    } else {
      setForm({ name: "", code: "", cnpj: "", contact: "", phone: "", email: "", responsibles: [] });
    }
  }, [client]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const addResponsible = () => {
    const name = responsibleInput.trim();
    if (!name) return;
    setForm((prev) => ({ ...prev, responsibles: [...prev.responsibles, name] }));
    setResponsibleInput("");
  };

  const removeResponsible = (idx) => {
    setForm((prev) => ({ ...prev, responsibles: prev.responsibles.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Nome do cliente e obrigatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      let res;
      if (isNew) {
        res = await api.post("/clients", payload);
      } else {
        res = await api.put(`/clients/${client.id}`, payload);
      }
      toast.success(isNew ? "Cliente criado com sucesso" : "Cliente atualizado");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao salvar cliente");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isNew && confirm(`Excluir cliente "${form.name}"?`)) {
      try {
        await api.delete(`/clients/${client.id}`);
        toast.success("Cliente excluido");
        onDeleted?.(client.id);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao excluir");
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nome *</Label>
          <Input value={form.name} onChange={handleChange("name")} placeholder="Nome do cliente" />
        </div>
        <div>
          <Label>Codigo</Label>
          <Input value={form.code} onChange={handleChange("code")} placeholder="Codigo" />
        </div>
        <div>
          <Label>CNPJ</Label>
          <Input value={form.cnpj} onChange={handleChange("cnpj")} placeholder="00.000.000/0000-00" />
        </div>
        <div>
          <Label>Contato</Label>
          <Input value={form.contact} onChange={handleChange("contact")} placeholder="Nome do contato" />
        </div>
        <div>
          <Label>Telefone</Label>
          <Input value={form.phone} onChange={handleChange("phone")} placeholder="(00) 0000-0000" />
        </div>
        <div>
          <Label>E-mail</Label>
          <Input value={form.email} onChange={handleChange("email")} type="email" placeholder="email@exemplo.com" />
        </div>
      </div>

      <div>
        <Label>Responsaveis</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={responsibleInput}
            onChange={(e) => setResponsibleInput(e.target.value)}
            placeholder="Adicionar responsavel"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addResponsible(); } }}
          />
          <Button type="button" variant="outline" onClick={addResponsible}>Adicionar</Button>
        </div>
        {form.responsibles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {form.responsibles.map((name, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs">
                {name}
                <button onClick={() => removeResponsible(idx)} className="text-muted-foreground hover:text-destructivo">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {(canSave || canDelete) && (
        <div className="flex items-center gap-2 pt-3 border-t border-border">
          {canSave && (
            <Button onClick={handleSave} disabled={saving} data-testid="client-form-save">
              <Save className="h-4 w-4 mr-1" />
              {isNew ? "Criar Cliente" : "Salvar Alteracoes"}
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} data-testid="client-form-delete">
              <Trash2 className="h-4 w-4 mr-1" />Excluir
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
